import React from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import type { TrackingTaskLocation } from '../../types/tracking';
import { formatDateTime } from '../../utils/dateHelpers';

interface TaskMarkerProps {
  taskLocation: TrackingTaskLocation;
}

function createTaskIcon(taskCount: number) {
  return L.divIcon({
    className: '',
    html: `
      <div style="position:relative;width:36px;height:46px;">
        <!-- Pulse ring -->
        <div style="
          position:absolute;top:6px;left:4px;
          width:28px;height:28px;border-radius:50%;
          background:rgba(124,58,237,0.2);
          animation:task-pulse 2s ease-out infinite;
        "></div>
        <!-- Pin body -->
        <svg width="36" height="46" viewBox="0 0 36 46" fill="none" xmlns="http://www.w3.org/2000/svg">
          <filter id="pin-shadow">
            <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.3"/>
          </filter>
          <g filter="url(#pin-shadow)">
            <path d="M18 44 C18 44 4 26 4 16 C4 8.268 10.268 2 18 2 C25.732 2 32 8.268 32 16 C32 26 18 44 18 44Z"
              fill="#7c3aed" stroke="#fff" stroke-width="2"/>
            <!-- Clipboard icon -->
            <rect x="12" y="10" width="12" height="15" rx="1.5" fill="none" stroke="#fff" stroke-width="1.5"/>
            <rect x="14.5" y="7.5" width="7" height="4" rx="1" fill="#7c3aed" stroke="#fff" stroke-width="1.2"/>
            <line x1="15" y1="17" x2="21" y2="17" stroke="#fff" stroke-width="1.2" stroke-linecap="round"/>
            <line x1="15" y1="20" x2="21" y2="20" stroke="#fff" stroke-width="1.2" stroke-linecap="round"/>
            <!-- Checkmark on bottom line -->
            <polyline points="15,23 16.5,24.5 19,21.5" fill="none" stroke="#4ade80" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
          </g>
        </svg>
        <!-- Count badge -->
        <span style="
          position:absolute;top:0;right:0;
          min-width:18px;height:18px;border-radius:9px;
          background:#16a34a;color:#fff;
          display:flex;align-items:center;justify-content:center;
          font-size:10px;font-weight:700;
          border:2px solid #fff;
          box-shadow:0 1px 3px rgba(0,0,0,0.3);
          padding:0 3px;
          line-height:1;
        ">${taskCount}</span>
        <style>
          @keyframes task-pulse {
            0% { transform:scale(1); opacity:0.6; }
            100% { transform:scale(2.2); opacity:0; }
          }
        </style>
      </div>`,
    iconSize: [36, 46],
    iconAnchor: [18, 46],
  });
}

export const TaskMarker: React.FC<TaskMarkerProps> = ({ taskLocation }) => {
  const icon = createTaskIcon(taskLocation.tasks.length);

  return (
    <Marker
      position={[taskLocation.latitude, taskLocation.longitude]}
      icon={icon}
      zIndexOffset={600}
    >
      <Popup minWidth={240} maxWidth={320}>
        <div className="p-1">
          {/* Header */}
          <div className="flex items-center gap-1.5 mb-2 pb-2 border-b border-gray-200">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex-shrink-0">
              {taskLocation.tasks.length}
            </span>
            <h3 className="font-semibold text-sm text-gray-900">
              {taskLocation.tasks.length === 1 ? 'Task Completed' : 'Tasks Completed'}
            </h3>
          </div>

          {/* Date/Time */}
          <div className="flex items-center gap-1 mb-2 text-xs text-gray-500">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{formatDateTime(taskLocation.recorded_at)}</span>
          </div>

          {/* Task list */}
          <div className="space-y-2">
            {taskLocation.tasks.map(t => (
              <div
                key={t.id}
                className="rounded-md bg-gray-50 p-2"
              >
                <div className="flex items-start gap-1.5">
                  <span className="mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-green-100 text-green-600 flex-shrink-0">
                    <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <div className="min-w-0">
                    <h4 className="font-medium text-sm text-gray-900 leading-tight">{t.title}</h4>
                    {t.description && (
                      <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{t.description}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Remarks */}
          {taskLocation.remarks && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <p className="text-xs font-medium text-gray-600 mb-0.5">Remarks</p>
              <p className="text-xs text-gray-500 italic leading-relaxed">{taskLocation.remarks}</p>
            </div>
          )}
        </div>
      </Popup>
    </Marker>
  );
};
